import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
import { Link, useNavigate } from "react-router-dom"

import { useBrand } from "../../theme/useBrand"

const makeGlowVariants = (rgb: string): Variants => ({
  idle: { textShadow: `0 0 0 rgba(${rgb}, 0)` },
  pulse: {
    textShadow: [
      `0 0 0 rgba(${rgb}, 0)`,
      `0 0 8px rgba(${rgb}, 0.35)`,
      `0 0 16px rgba(${rgb}, 0.6)`,
      `0 0 20px rgba(${rgb}, 0.75)`,
      `0 0 12px rgba(${rgb}, 0.5)`,
      `0 0 0 rgba(${rgb}, 0)`,
    ],
    transition: {
      duration: 6,
      ease: "easeInOut",
      times: [0, 0.28, 0.48, 0.62, 0.8, 1],
      repeat: Infinity,
      repeatDelay: 1.6,
    },
  },
})

/** Ambient sparkle placements for the hero's negative space (percent coords). */
const HERO_SPARKS = [
  { top: "16%", left: "12%", delay: "0s", scale: 1.4 },
  { top: "22%", left: "78%", delay: "1.8s", scale: 1 },
  { top: "34%", left: "88%", delay: "0.9s", scale: 0.8 },
  { top: "12%", left: "52%", delay: "2.6s", scale: 0.8 },
  { top: "78%", left: "6%", delay: "1.2s", scale: 1 },
  { top: "82%", left: "84%", delay: "0.4s", scale: 1.4 },
] as const

const MotionLink = motion(Link)

const GLYPHS = "!<>-_\\/#$%&=+*?@[]{}~"

const randomGlyph = () => {
  const char = GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
  return char ?? "*"
}

const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min

type GlyphAnimationOptions = {
  charDelay?: number
  scrambleDuration?: number
  scrambleInterval?: number
  startBlank?: boolean
}

type GlyphAnimator = {
  characters: string[]
  display: string[]
  triggerOnce: () => void
  startLoop: () => void
  stopLoop: () => void
}

function useGlyphAnimator(text: string, options?: GlyphAnimationOptions): GlyphAnimator {
  const { charDelay = 140, scrambleDuration = 320, scrambleInterval = 38, startBlank = false } = options ?? {}
  const characters = useMemo(() => text.split(""), [text])

  const computeState = useCallback(
    (mode: "blank" | "final") =>
      characters.map(char => {
        if (char === "\n") return ""
        if (char === " ") return " "
        return mode === "final" ? char : ""
      }),
    [characters],
  )

  const [display, setDisplay] = useState<string[]>(() => computeState(startBlank ? "blank" : "final"))
  const timeoutsRef = useRef<number[]>([])
  const intervalsRef = useRef<Array<number | null>>([])
  const loopActiveRef = useRef(false)

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach(id => window.clearTimeout(id))
    timeoutsRef.current = []
    intervalsRef.current.forEach(id => {
      if (id !== null) {
        window.clearInterval(id)
      }
    })
    intervalsRef.current = []
    loopActiveRef.current = false
  }, [])

  const setFinalText = useCallback(() => {
    setDisplay(computeState("final"))
  }, [computeState])

  const setCharacter = useCallback((index: number, value: string) => {
    setDisplay(prev => {
      if (prev[index] === value) return prev
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const triggerOnce = useCallback(() => {
    clearTimers()
    loopActiveRef.current = false
    setDisplay(computeState("blank"))

    const intervals: Array<number | null> = characters.map(() => null)
    intervalsRef.current = intervals

    characters.forEach((char, index) => {
      if (char === "\n") return

      if (char === " ") {
        const spaceTimeout = window.setTimeout(() => {
          setCharacter(index, " ")
        }, index * charDelay)
        timeoutsRef.current.push(spaceTimeout)
        return
      }

      const startTimeout = window.setTimeout(() => {
        intervalsRef.current[index] = window.setInterval(() => {
          setCharacter(index, randomGlyph())
        }, scrambleInterval)

        const settleTimeout = window.setTimeout(() => {
          const intervalId = intervalsRef.current[index]
          if (intervalId !== null) {
            window.clearInterval(intervalId)
            intervalsRef.current[index] = null
          }
          setCharacter(index, char)
        }, scrambleDuration)

        timeoutsRef.current.push(settleTimeout)
      }, index * charDelay)

      timeoutsRef.current.push(startTimeout)
    })

    const finalizeTimeout = window.setTimeout(() => {
      setFinalText()
    }, characters.length * charDelay + scrambleDuration + 24)

    timeoutsRef.current.push(finalizeTimeout)
  }, [characters, charDelay, computeState, scrambleDuration, scrambleInterval, clearTimers, setCharacter, setFinalText])

  const startLoop = useCallback(() => {
    clearTimers()
    loopActiveRef.current = true

    const intervals: Array<number | null> = characters.map(() => null)
    intervalsRef.current = intervals

    characters.forEach((char, index) => {
      if (char === "\n") return
      if (char === " ") {
        setCharacter(index, " ")
        return
      }
      intervalsRef.current[index] = window.setInterval(() => {
        setCharacter(index, randomGlyph())
      }, scrambleInterval)
    })
  }, [characters, clearTimers, scrambleInterval, setCharacter])

  const stopLoop = useCallback(() => {
    if (!loopActiveRef.current) return
    clearTimers()
    setFinalText()
  }, [clearTimers, setFinalText])

  useEffect(() => {
    setDisplay(computeState(startBlank ? "blank" : "final"))
    clearTimers()
  }, [characters, computeState, clearTimers, startBlank])

  useEffect(() => clearTimers, [clearTimers])

  return { characters, display, triggerOnce, startLoop, stopLoop }
}

type GlyphDisplayProps = {
  characters: string[]
  display: string[]
  prefix: string
  breakClassName?: string
  /** Extra className per exact word (e.g. { "Demon": "tagline-strike" }) */
  wordDecorations?: Record<string, string>
}

function GlyphDisplay({ characters, display, prefix, breakClassName, wordDecorations }: GlyphDisplayProps) {
  const segments: Array<{ type: "br" | "space" | "word"; start: number; length: number }> = []
  characters.forEach((char, index) => {
    if (char === "\n") {
      segments.push({ type: "br", start: index, length: 1 })
    } else if (char === " ") {
      segments.push({ type: "space", start: index, length: 1 })
    } else {
      const last = segments[segments.length - 1]
      if (last && last.type === "word" && last.start + last.length === index) {
        last.length += 1
      } else {
        segments.push({ type: "word", start: index, length: 1 })
      }
    }
  })

  const renderChar = (index: number) => {
    const value = display[index]
    return (
      <span key={`${prefix}-ch-${index}`} className="glitch-typewriter-char">
        {value === "" ? "\u00a0" : value}
      </span>
    )
  }

  return (
    <>
      {segments.map((segment) => {
        if (segment.type === "br") {
          return <br key={`${prefix}-br-${segment.start}`} className={breakClassName} />
        }
        if (segment.type === "space") {
          return renderChar(segment.start)
        }
        const word = characters.slice(segment.start, segment.start + segment.length).join("")
        const decoration = wordDecorations?.[word]
        return (
          <span
            key={`${prefix}-w-${segment.start}`}
            className={decoration ? `glitch-typewriter-word ${decoration}` : "glitch-typewriter-word"}
          >
            {Array.from({ length: segment.length }, (_, offset) => renderChar(segment.start + offset))}
          </span>
        )
      })}
    </>
  )
}

type GlitchyTextProps = {
  text: string
  minInterval?: number
  maxInterval?: number
  charDelay?: number
  scrambleDuration?: number
  scrambleInterval?: number
  breakClassName?: string
  triggerToken?: number | null
}

function GlitchyText({
  text,
  minInterval = 4600,
  maxInterval = 7800,
  charDelay = 48,
  scrambleDuration = 260,
  scrambleInterval = 32,
  breakClassName,
  triggerToken,
}: GlitchyTextProps) {
  const { characters, display, triggerOnce } = useGlyphAnimator(text, {
    charDelay,
    scrambleDuration,
    scrambleInterval,
  })

  useEffect(() => {
    triggerOnce()

    let disposed = false
    let timeoutId: number | null = null

    const intervalLow = Math.max(1200, Math.min(minInterval, maxInterval))
    const intervalHigh = Math.max(intervalLow, Math.max(minInterval, maxInterval))

    const schedule = (delay: number) => {
      if (disposed) return
      timeoutId = window.setTimeout(() => {
        triggerOnce()
        schedule(randomBetween(intervalLow, intervalHigh))
      }, delay)
    }

    schedule(randomBetween(1600, 2600))

    return () => {
      disposed = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [maxInterval, minInterval, triggerOnce])

  useEffect(() => {
    if (triggerToken == null) return
    triggerOnce()
  }, [triggerToken, triggerOnce])

  return (
    <span className="glitch-typewriter">
      <GlyphDisplay characters={characters} display={display} prefix="glitch-text" breakClassName={breakClassName} />
    </span>
  )
}

type GlitchTypewriterProps = {
  text: string
  charDelay?: number
  scrambleDuration?: number
  scrambleInterval?: number
  triggerToken?: number | null
}

function GlitchTypewriter({
  text,
  charDelay = 140,
  scrambleDuration = 320,
  scrambleInterval = 38,
  triggerToken,
}: GlitchTypewriterProps) {
  const { characters, display, triggerOnce } = useGlyphAnimator(text, {
    charDelay,
    scrambleDuration,
    scrambleInterval,
    startBlank: true,
  })

  useEffect(() => {
    triggerOnce()
  }, [triggerOnce])

  useEffect(() => {
    if (triggerToken == null) return
    triggerOnce()
  }, [triggerToken, triggerOnce])

  return (
    <span className="glitch-typewriter">
      <GlyphDisplay characters={characters} display={display} prefix="glitch-type" />
    </span>
  )
}

export default function MarketingHome() {
  const navigate = useNavigate()
  const brand = useBrand()
  const personaWord = brand.isFairy ? "Fairy" : "Demon"
  const glowRgb = brand.isFairy ? "46, 214, 181" : "255, 43, 79"
  const glowVariants = useMemo(() => makeGlowVariants(glowRgb), [glowRgb])
  const [showCtas, setShowCtas] = useState(false)
  const [glowPhase, setGlowPhase] = useState<"idle" | "pulse">("idle")
  const [isExiting, setIsExiting] = useState(false)
  const [exitToken, setExitToken] = useState<number | null>(null)
  const exitTimeoutRef = useRef<number | null>(null)
  const {
    characters: getStartedChars,
    display: getStartedDisplay,
    startLoop: startGetStartedLoop,
    stopLoop: stopGetStartedLoop,
  } = useGlyphAnimator("Get started", {
    charDelay: 42,
    scrambleDuration: 260,
    scrambleInterval: 28,
  })
  const {
    characters: howChars,
    display: howDisplay,
    startLoop: startHowLoop,
    stopLoop: stopHowLoop,
  } = useGlyphAnimator("How it works", {
    charDelay: 42,
    scrambleDuration: 260,
    scrambleInterval: 28,
  })
  const {
    characters: loginChars,
    display: loginDisplay,
    startLoop: startLoginLoop,
    stopLoop: stopLoginLoop,
  } = useGlyphAnimator("Login", {
    charDelay: 38,
    scrambleDuration: 220,
    scrambleInterval: 26,
  })
  const taglineText = `Your invoicing and collections ${personaWord} Agent, that lives in your messages.`
  const {
    characters: taglineChars,
    display: taglineDisplay,
    triggerOnce: triggerTagline,
  } = useGlyphAnimator(taglineText, {
    charDelay: 36,
    scrambleDuration: 280,
    scrambleInterval: 26,
  })

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current !== null) {
        window.clearTimeout(exitTimeoutRef.current)
      }
      stopGetStartedLoop()
      stopHowLoop()
      stopLoginLoop()
    }
  }, [stopGetStartedLoop, stopHowLoop, stopLoginLoop])

  useEffect(() => {
    const timer = window.setTimeout(() => setShowCtas(true), 1900)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!showCtas) return
    const timer = window.setTimeout(() => {
      setGlowPhase("pulse")
    }, 1300)

    return () => {
      window.clearTimeout(timer)
    }
  }, [showCtas])

  const handleGetStartedClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      if (isExiting) return
      setIsExiting(true)
      setExitToken(prev => (prev ?? 0) + 1)
      triggerTagline()
      startGetStartedLoop()
      startHowLoop()
      startLoginLoop()

      exitTimeoutRef.current = window.setTimeout(() => {
        stopGetStartedLoop()
        stopHowLoop()
        stopLoginLoop()
        navigate("/onboard/intro")
      }, 1600)
    },
    [
      isExiting,
      triggerTagline,
      startGetStartedLoop,
      startHowLoop,
      startLoginLoop,
      stopGetStartedLoop,
      stopHowLoop,
      stopLoginLoop,
      navigate,
    ],
  )

  return (
    <>
      <div className="rail-top" />
      <div className="rail-bottom" />

      <motion.header
        className="home-topbar"
        initial={{ opacity: 0, y: -8 }}
        animate={isExiting ? { opacity: 0, y: -8 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut", delay: 0.4 }}
      >
        <Link to="/" className="home-topbar__brand" aria-label={`${brand.name} home`}>
          <img src={brand.sprite} width={18} height={18} className="pixel" alt="" />
          <span>{brand.wordmark}</span>
        </Link>
        <nav className="home-topbar__nav" aria-label="Primary">
          <Link to="/how-it-works">How it works</Link>
          <Link to="/pricing" className="home-topbar__nav-secondary">Pricing</Link>
          <Link to="/login">Login</Link>
        </nav>
      </motion.header>

      <main className="container-hero">
        <div className="hero-ambient" aria-hidden>
          {HERO_SPARKS.map((spark) => (
            <span
              key={`${spark.top}-${spark.left}`}
              className="sparkle-dot hero-ambient__spark"
              style={{
                top: spark.top,
                left: spark.left,
                animationDelay: spark.delay,
                transform: `scale(${spark.scale})`,
              }}
            />
          ))}
        </div>
        <div className="hero-frame">
          <motion.div
            className="hero-content"
            animate={isExiting ? { opacity: 0, y: -18 } : { opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="sprite-sigil" style={{ marginBottom: "16px" }}>
              <img src={brand.sprite} width={24} height={24} className="pixel" alt="" />
            </div>

            <motion.h1
              className="brand-xl"
              style={{ marginBottom: "12px" }}
              aria-label={brand.name}
              variants={glowVariants}
              initial="idle"
              animate={glowPhase}
            >
              <GlitchTypewriter
                key={personaWord}
                text={personaWord.toUpperCase()}
                triggerToken={exitToken}
              />
            </motion.h1>

            <div className="brand-small" style={{ marginBottom: "32px" }}>
              <GlitchyText
                key={personaWord}
                text={brand.isFairy ? "/ˈfeə.ri/" : "/ˈdiː.mən/"}
                triggerToken={exitToken}
              />
            </div>

            <p className="hero-tagline text-muted">
              <span className="sr-only">{taglineText}</span>
              <span className="glitch-typewriter" aria-hidden="true">
                <GlyphDisplay
                  characters={taglineChars}
                  display={taglineDisplay}
                  prefix="tagline"
                  breakClassName="hero-tagline-break"
                  wordDecorations={{ [personaWord]: "tagline-strike", "Agent,": "tagline-agent" }}
                />
              </span>
            </p>

            <div
              className="cta-row"
              style={{
                pointerEvents: isExiting ? "none" : showCtas ? "auto" : "none",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                width: "min(420px, 100%)",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: showCtas ? 1 : 0, y: showCtas ? 0 : 12 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: showCtas ? 0.2 : 0 }}
              >
                <MotionLink
                  to="/onboard/intro"
                  aria-label="Get started"
                  className="btn-lg btn-primary"
                  style={{ width: "100%" }}
                  onPointerEnter={startGetStartedLoop}
                  onPointerLeave={stopGetStartedLoop}
                  onFocus={startGetStartedLoop}
                  onBlur={stopGetStartedLoop}
                  onTouchStart={startGetStartedLoop}
                  onTouchEnd={stopGetStartedLoop}
                  onTouchCancel={stopGetStartedLoop}
                  onClick={handleGetStartedClick}
                  whileHover={{
                    scale: 1.04,
                    boxShadow: `0 0 0 1px rgba(10, 10, 11, 0.65), 0 0 28px rgba(${glowRgb}, 0.5)`,
                  }}
                  whileFocus={{
                    boxShadow: `0 0 0 1px rgba(10, 10, 11, 0.65), 0 0 28px rgba(${glowRgb}, 0.5)`,
                  }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.18 }}
                >
                  <span className="glitch-typewriter">
                    <GlyphDisplay
                      characters={getStartedChars}
                      display={getStartedDisplay}
                      prefix="cta-start"
                    />
                  </span>
                </MotionLink>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: showCtas ? 1 : 0, y: showCtas ? 0 : 12 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: showCtas ? 0.45 : 0 }}
              >
                <MotionLink
                  to="/login"
                  aria-label="Login"
                  className="btn-lg btn-secondary"
                  style={{ width: "100%" }}
                  onPointerEnter={startLoginLoop}
                  onPointerLeave={stopLoginLoop}
                  onFocus={startLoginLoop}
                  onBlur={stopLoginLoop}
                  onTouchStart={startLoginLoop}
                  onTouchEnd={stopLoginLoop}
                  onTouchCancel={stopLoginLoop}
                  whileHover={{
                    scale: 1.03,
                    borderColor: `rgba(${glowRgb}, 0.6)`,
                    color: "var(--accent-2-text)",
                    boxShadow: `0 0 0 1px rgba(${glowRgb}, 0.4)`,
                  }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.18 }}
                >
                  <span className="glitch-typewriter">
                    <GlyphDisplay characters={loginChars} display={loginDisplay} prefix="cta-login" />
                  </span>
                </MotionLink>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: showCtas ? 1 : 0, y: showCtas ? 0 : 12 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: showCtas ? 0.7 : 0 }}
                style={{ gridColumn: "1 / -1" }}
              >
                <MotionLink
                  to="/how-it-works"
                  aria-label="How it works"
                  className="btn-lg"
                  style={{
                    width: "100%",
                    border: "1px dashed var(--accent)",
                    background: "transparent",
                    color: "var(--accent-text)",
                    gap: "10px",
                  }}
                  onPointerEnter={startHowLoop}
                  onPointerLeave={stopHowLoop}
                  onFocus={startHowLoop}
                  onBlur={stopHowLoop}
                  onTouchStart={startHowLoop}
                  onTouchEnd={stopHowLoop}
                  onTouchCancel={stopHowLoop}
                  whileHover={{
                    scale: 1.02,
                    boxShadow: `0 0 0 1px rgba(${glowRgb}, 0.4)`,
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                >
                  <span aria-hidden>▶</span>
                  <span className="glitch-typewriter">
                    <GlyphDisplay characters={howChars} display={howDisplay} prefix="cta-how" />
                  </span>
                </MotionLink>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </main>

      <motion.div
        id="how"
        className="steps"
        animate={isExiting ? { opacity: 0, y: 12 } : { opacity: 1, y: 0 }}
        transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <span className="num">01</span>
        <span className="lbl">Verify Number</span>
        <span className="num">02</span>
        <span className="lbl">Connect Stripe</span>
        <span className="num">03</span>
        <span className="lbl">{brand.isFairy ? "Summon Fairies" : "Summon Demons"}</span>
      </motion.div>
    </>
  )
}
